import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { RefundStatus } from 'src/constants/payments-ops/enums'
import { refundStatusLabels } from 'src/constants/payments-ops/labels'
import { buildRefundColumns } from 'src/constants/payments-ops/refundsGrid'
import type { Refund } from 'src/interfaces/refund'
import { getRefunds, getRefundsLoading, getRefundsPagination } from 'src/selectors/refund'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'

interface Props {
  status: RefundStatus
  paginationModel: GridPaginationModel
  canExecute: boolean
  getExecuteDisabledReason(refund: Refund): string | null
  onPaginationModelChange(model: GridPaginationModel): void
  onExecute(refund: Refund): void
}

const getRowId = (row: Refund): string => {
  return row.id
}

const RefundsTableComponent = ({
  status,
  paginationModel,
  canExecute,
  getExecuteDisabledReason,
  onPaginationModelChange,
  onExecute,
}: Props) => {
  const refunds = useSelector(getRefunds)
  const pagination = useSelector(getRefundsPagination)
  const isLoading = useSelector(getRefundsLoading)
  const nameMap = usePartnerNameMap()

  const columns = useMemo(() => {
    return buildRefundColumns({ status, nameMap, canExecute, onExecute, getExecuteDisabledReason })
  }, [canExecute, getExecuteDisabledReason, nameMap, onExecute, status])

  const renderEmpty = useMemo(() => {
    const label = refundStatusLabels[status].label.toLowerCase()

    return () => {
      return <EmptyTableMessage message={`No ${label} refunds.`} />
    }
  }, [status])

  return (
    <GridTable<Refund>
      rows={refunds}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      paginationMode="server"
      rowCount={pagination.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      sortingMode="server"
      EmptyElement={renderEmpty}
      tableDataTestId="refunds-table"
      rowDataTestIdPrefix="refunds"
    />
  )
}

export const RefundsTable = memo(RefundsTableComponent)
